import { produce } from 'immer';
import {
  getTopLevelContainer,
  mutateAddColumns,
} from '../utils/columns';
import {
  computeContainerHeight,
  mutateDropInContainer,
  mutateUpdateContainer,
} from '../utils/containers';
import {
  initializeBoxContent,
  isATitleBox,
  isAVariableBox,
  mutateAddBoxes,
  mutateRemoveBox,
} from '../utils/boxes';
import { sortVariableOrderArray } from '../utils/variableOrderArray';
import { ItemTypes } from '../constants/constants';
import { message } from 'antd';
import { editFreezeHolder } from '../collab/structureLocks';

const checkIfTitleBoxCanDrop = ({ newBoxes, drawMode, boxes, columnId }) => {
  if (isATitleBox(newBoxes[0].type) && drawMode) {
    const boxesInContainer = boxes.filter((b) => b.columnId === columnId);
    const topTitleBoxInDrawModeContainer = boxesInContainer
      .filter((b) => isATitleBox(b.type))
      .reduce((minBox, currentBox) => {
        return minBox === null || currentBox.top < minBox.top
          ? currentBox
          : minBox;
      }, null);

    const titleIndices = {
      [ItemTypes.TITLE]: 1,
      [ItemTypes.TITLE_2]: 2,
      [ItemTypes.TITLE_3]: 3,
      [ItemTypes.TITLE_4]: 4,
    };

    if (
      topTitleBoxInDrawModeContainer &&
      titleIndices[newBoxes[0].type] >
        titleIndices[topTitleBoxInDrawModeContainer.type] &&
      newBoxes[0].top < topTitleBoxInDrawModeContainer.top
    ) {
      return false;
    }
  }
};

export const columnsActions = (set, get) => ({
  /* COLUMNS */
  addColumns: (newColumns = []) => {
    set(
      produce((draft) => {
        mutateAddColumns(draft, newColumns);
      }),
      false,
      `add ${newColumns.length} new Columns`
    );
  },
  dropBoxInColumn: ({
    id,
    drawMode = false,
    updateContainerHeight = false,
    newBox,
    maskId = null,
    boxIdToReplace,
  }) => {
    // Collab : un co-éditeur configure ou charge une section → création gelée
    const freeze = editFreezeHolder(get());
    if (freeze) {
      message.warning(freeze.message);
      return;
    }
    set(
      produce((draft) => {
        const column = draft.columns.find((c) => c.id === id);
        const newBoxes = newBox.type === ItemTypes.DRAW ? [] : [newBox];
        if (
          checkIfTitleBoxCanDrop({
            newBoxes,
            drawMode,
            boxes: draft.boxes,
            columnId: id,
          }) === false
        )
          return false;
        if (updateContainerHeight) {
          const container = draft.containers.find((c) =>
            c.columnsIds.includes(id)
          );
          const containerHeight = computeContainerHeight({
            drawMode,
            landscape: draft.landscape,
            boxType: newBox.type,
          });
          if (container) {
            mutateUpdateContainer(draft, {
              ...container,
              height: containerHeight,
            });
          }
        }

        // Mutation directe : spreader le draft capturerait ses sous-objets
        // (rowContainerIds) sous forme de proxies, révoqués en fin de produce.
        if (column) column.drawMode = drawMode;

        if (boxIdToReplace) {
          mutateRemoveBox(draft, boxIdToReplace);
        }
        if (newBox.type === ItemTypes.GROUP_BOXES) {
          const boxes = [
            ...draft.boxes,
            ...draft.masks.map((m) => m.boxes).flat(),
          ];
          const fromCopy = boxes.find(
            (b) =>
              b.type === ItemTypes.GROUP_BOXES &&
              [...b.boxIds].sort().join(',') ===
                [...newBox.boxIds].sort().join(',')
          );
          const boxesFromGroup = boxes.filter((b) =>
            fromCopy.boxIds.includes(b.id)
          );
          const boxIds = [];
          boxesFromGroup.forEach((b) => {
            const newBoxFromGroup = initializeBoxContent({
              box: b,
              configuration: draft.configuration,
              mask:
                maskId === null
                  ? null
                  : draft.masks.find((m) => m.id === maskId),
              column,
              parentId: maskId ?? newBox.columnId,
            });
            newBoxFromGroup.left += newBox.left - fromCopy.left;
            newBoxFromGroup.top += newBox.top - fromCopy.top;
            newBoxFromGroup.groupTop = newBox.top;
            newBoxFromGroup.originalLeft += newBox.left - fromCopy.left;
            newBoxFromGroup.originalTop += newBox.top - fromCopy.top;
            boxIds.push(newBoxFromGroup.id);
            mutateAddBoxes({
              draft,
              newBoxes: [newBoxFromGroup],
              drawMode,
              maskId,
            });
          });
          newBox.boxIds = boxIds;
        }
        mutateAddBoxes({ draft, newBoxes, drawMode, maskId });
        if (!maskId) {
          // Sous-colonne d'une ligne imbriquée : la sélection ne connaît que
          // les containers top-level, on remonte donc jusqu'à celui qui héberge
          // la ligne.
          const container = getTopLevelContainer({
            columnId: id,
            containers: draft.containers,
            rowContainers: draft.rowContainers,
          });
          if (container) mutateDropInContainer({ draft, container, box: newBox });
        }
        // Réordonner variableOrderArray si une box variable a été ajoutée
        if (isAVariableBox(newBox.type) || (newBox.type === ItemTypes.GROUP_BOXES && newBox.boxIds?.some((boxId) => {
          const allBoxes = [...draft.boxes, ...draft.masks.map((m) => m.boxes).flat()];
          const box = allBoxes.find((b) => b.id === boxId);
          return box && isAVariableBox(box.type);
        }))) {
          sortVariableOrderArray(draft);
        }
      }),
      false,
      `Drop box in column ${id}`
    );
  },
  // Répartition verticale des box d'une cellule (colonne ou cellule de ligne
  // imbriquée) : `top | center | bottom | between`. Hors mode dessin une box n'a
  // pas de position libre — répartir en déplaçant les box une fois n'aurait
  // nulle part où être stocké et sauterait à la première frappe. C'est donc une
  // règle de mise en page, portée par la cellule et rejouée à chaque rendu
  // (édition, aperçu, PDF). `center` reste le défaut historique.
  setColumnVerticalAlign: (id, verticalAlign) => {
    set(
      produce((draft) => {
        const column = draft.columns.find((c) => c.id === id);
        if (column) column.verticalAlign = verticalAlign;
      }),
      false,
      `Set vertical align of column ${id} to ${verticalAlign}`
    );
  },
  setColumnBackgroundColor: (id, backgroundColor) => {
    set(
      produce((draft) => {
        const column = draft.columns.find((c) => c.id === id);
        if (column) column.backgroundColor = backgroundColor;
      }),
      false,
      `Set background color of column ${id}`
    );
  },
});
